import React, { useEffect, useState, useCallback } from 'react';
import './PriceEditor.css';

// Pre-order date (quests.opens_at), test and prod side by side. Same shape
// as PriceEditor: each is written straight to its table, the test→prod sync
// never copies it. While the date is in the future the app sells the quest
// but keeps gameplay closed; on the date it opens by itself.

// '2026-09-28' → '28 Sep 2026'
const fmtDate = (iso) => {
  if (!iso) return 'open';
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Keep the whole server message: a bare "HTTP 500" explains nothing.
const errorText = async (r) => {
  const body = await r.text();
  try {
    return JSON.parse(body).error || body;
  } catch {
    return `HTTP ${r.status}: ${body.slice(-2000)}`;
  }
};

const OpensAtField = ({ target, row, today, canEdit, onSave }) => {
  const rowDate = row ? row.opens_at : null;
  const rowId = row ? row.quest_id : null;
  const [value, setValue] = useState(rowDate || '');
  const [status, setStatus] = useState('idle'); // idle | saving | saved | error
  const [error, setError] = useState(null);

  useEffect(() => { setValue(rowDate || ''); }, [rowDate]);
  useEffect(() => { setStatus('idle'); setError(null); }, [rowId]);

  if (!row) {
    return (
      <div className="price-field">
        <span className="price-field__label">{target === 'prod' ? 'Prod' : 'Test'}</span>
        <span className="price-field__missing">not in {target}</span>
      </div>
    );
  }

  const next = value || null;
  const dirty = next !== (row.opens_at || null);
  const busy = status === 'saving';

  const save = async (nextValue) => {
    if (nextValue === (row.opens_at || null)) return;
    if (target === 'prod') {
      const msg = nextValue && nextValue > today
        ? `Pre-order for ${row.quest_id} until ${fmtDate(nextValue)}.\n\n` +
          'The app switches immediately: the quest stays on sale, gameplay is closed ' +
          `and it opens by itself on ${fmtDate(nextValue)}.\n\n` +
          'Push the final content with Sync to prod before that date, otherwise ' +
          'the version now in prod is what opens.\n\nSave?'
        : `Open ${row.quest_id} in the app now?\n\n` +
          'Buyers get the "Start quest" button immediately, with the content now in prod.';
      if (!window.confirm(msg)) return;
    }
    setStatus('saving');
    setError(null);
    try {
      await onSave(target, nextValue, row.opens_at || null);
      setStatus('saved');
    } catch (e) {
      setStatus('error');
      setError(e.message);
    }
  };

  return (
    <div className="price-field">
      <span className="price-field__label">
        {target === 'prod' ? 'Prod' : 'Test'}
        <span className="price-field__qid">{row.quest_id}</span>
      </span>
      <div className="price-field__row">
        <input
          type="date"
          className="price-field__input opens-at__input"
          value={value}
          min={today}
          disabled={!canEdit || busy}
          onChange={(e) => { setValue(e.target.value); setStatus('idle'); }}
        />
        <button
          className={`save-button price-field__save ${status}`}
          disabled={!canEdit || !dirty || busy}
          onClick={() => save(next)}
        >
          {busy ? 'Saving…' : status === 'saved' && !dirty ? 'Saved ✓' : 'Save'}
        </button>
        {row.opens_at && (
          <button
            className="save-button"
            disabled={!canEdit || busy}
            onClick={() => { setValue(''); save(null); }}
          >
            Open now
          </button>
        )}
      </div>
      <span className="price-field__hint">
        {row.pre_order
          ? `Pre-order until ${fmtDate(row.opens_at)}: on sale, gameplay closed`
          : 'Open: bought quests can be started'}
      </span>
      {error && <span className="price-field__error">{error}</span>}
      {!canEdit && (
        <span className="price-field__hint">
          {target === 'prod' ? 'Needs push-to-prod permission' : 'No access to the test quest'}
        </span>
      )}
    </div>
  );
};

const OpensAtEditor = ({ questId, selectedTable, onPreviewChange }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const table = selectedTable || 'quests_test';

  const load = useCallback(async () => {
    const r = await fetch(`/api/quest-opens-at/${encodeURIComponent(questId)}?table=${table}`, {
      credentials: 'include',
    });
    if (!r.ok) throw new Error(await errorText(r));
    return r.json();
  }, [questId, table]);

  // Saved test/prod rows for the app preview.
  useEffect(() => {
    if (onPreviewChange) onPreviewChange(data);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!questId) return undefined;
    let cancelled = false;
    setData(null);
    setError(null);
    load()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [questId, load]);

  const save = async (target, opensAt, expected) => {
    const r = await fetch(`/api/quest-opens-at/${encodeURIComponent(questId)}?table=${table}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ target, opens_at: opensAt, expected }),
    });
    if (!r.ok) {
      const msg = await errorText(r);
      // Someone changed it meanwhile: show the fresh value alongside the error.
      if (r.status === 409) load().then(setData).catch(() => {});
      throw new Error(msg);
    }
    const saved = await r.json();
    setData((d) => ({ ...d, [target]: saved.row }));
  };

  return (
    <div className="price-editor">
      <div className="price-editor__title">📅 Pre-order</div>
      {error && <div className="price-field__error">{error}</div>}
      {!data && !error && <div className="price-editor__loading">Loading…</div>}
      {data && (
        <div className="price-editor__grid">
          <OpensAtField target="test" row={data.test} today={data.today} canEdit={data.canEditTest} onSave={save} />
          <OpensAtField target="prod" row={data.prod} today={data.today} canEdit={data.canEditProd} onSave={save} />
        </div>
      )}
    </div>
  );
};

export default OpensAtEditor;
